import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from 'angular2-notifications';
import { ContractsApiService } from 'src/app/core/services/api/contracts-api.service';
import { ErrorHandlingService } from 'src/app/core/services/error-handling.service';

interface AchPaymentMethod {
  id: number | string;
  bank_name?: string;
  account_type?: string;
  account_holder_name?: string;
  pm_last_four?: string;
}

@Component({
  selector: 'app-ach-payment-methods-card',
  templateUrl: './ach-payment-methods-card.component.html',
  styleUrls: ['./ach-payment-methods-card.component.scss'],
})
export class AchPaymentMethodsCardComponent implements OnInit {
  methods: AchPaymentMethod[] = [];
  form: FormGroup;
  showForm = false;
  isLoading = false;
  isSaving = false;
  deletingId: number | string | null = null;

  accountTypeOptions = [
    { label: 'Checking', value: 'checking' },
    { label: 'Savings', value: 'savings' },
    { label: 'Business Checking', value: 'businessChecking' },
  ];

  constructor(
    private fb: FormBuilder,
    private contractsApiService: ContractsApiService,
    private notificationService: NotificationsService,
    private errorHandlingService: ErrorHandlingService
  ) {
    this.form = this.fb.group({
      account_holder_name: ['', Validators.required],
      bank_name: ['', Validators.required],
      account_type: ['checking', Validators.required],
      account_number: ['', Validators.required],
      routing_number: ['', [Validators.required, Validators.pattern(/^\d{9}$/)]],
    });
  }

  ngOnInit(): void {
    this.loadMethods();
  }

  loadMethods(): void {
    this.isLoading = true;
    this.contractsApiService.getMyPaymentMethods().subscribe({
      next: (res) => {
        this.methods = this.normalizeMethods(res?.data);
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorHandlingService.errorHandling(err);
      },
    });
  }

  toggleForm(): void {
    this.showForm = !this.showForm;
    if (!this.showForm) {
      this.resetForm();
    }
  }

  saveMethod(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.contractsApiService.createUserPaymentMethod(this.form.value).subscribe({
      next: (res) => {
        this.notificationService.success('', res?.message || 'ACH payment method added');
        this.isSaving = false;
        this.showForm = false;
        this.resetForm();
        this.loadMethods();
      },
      error: (err) => {
        this.isSaving = false;
        this.errorHandlingService.errorHandling(err);
      },
    });
  }

  deleteMethod(method: AchPaymentMethod): void {
    if (!method?.id || !window.confirm('Delete this ACH payment method?')) {
      return;
    }

    this.deletingId = method.id;
    this.contractsApiService.deleteUserPaymentMethod(method.id).subscribe({
      next: (res) => {
        this.notificationService.success('', res?.message || 'ACH payment method deleted');
        this.deletingId = null;
        this.loadMethods();
      },
      error: (err) => {
        this.deletingId = null;
        this.errorHandlingService.errorHandling(err);
      },
    });
  }

  accountTypeLabel(type?: string): string {
    return this.accountTypeOptions.find((option) => option.value === type)?.label || '-';
  }

  private resetForm(): void {
    this.form.reset({
      account_holder_name: '',
      bank_name: '',
      account_type: 'checking',
      account_number: '',
      routing_number: '',
    });
  }

  private normalizeMethods(data: any): AchPaymentMethod[] {
    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    return [];
  }
}
